import { RefreshCw } from 'lucide-react';

import { Button } from '~shared/shadcn/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '~shared/shadcn/ui/collapsible';

interface CrashScreenProps {
  /** Heading shown to the user. */
  title?: string;
  /** Supporting sentence shown to the user. */
  description?: string;
  /** The caught error. Used to render DEV-only technical details. */
  error?: unknown;
  /**
   * Sentry event id for this crash. Shown as a short reference code so users
   * can quote it to support and developers can jump straight to the issue.
   */
  eventId?: string;
  /** React component stack captured by the boundary, shown in DEV only. */
  componentStack?: string;
  /**
   * Reset the boundary and re-render the children (TanStack Router / Sentry
   * both expose this). When provided a "Try again" button is shown.
   */
  onRetry?: () => void;
  /** Label for the tertiary navigation action. */
  homeLabel?: string;
  /** Destination for the tertiary navigation action. */
  homeHref?: string;
}

function toErrorParts(error: unknown): {
  name: string;
  message: string;
  stack?: string;
} {
  if (error instanceof Error) {
    return {
      name: error.name || 'Error',
      message: error.message || 'Unknown error',
      stack: error.stack,
    };
  }
  return { name: 'Error', message: String(error) };
}

/**
 * Shared, branded crash UI used by both the top-level Sentry ErrorBoundary and
 * the router's default route error component. Replaces TanStack Router's raw
 * default crash box and the generic "Something went wrong" fallback.
 *
 * End users get a friendly message plus recovery actions; developers get a
 * collapsible technical section (DEV builds only) with name/message + stacks.
 */
export const CrashScreen = ({
  title = "We couldn't load this page",
  description,
  error,
  eventId,
  componentStack,
  onRetry,
  homeLabel = 'Go to dashboard',
  homeHref = '/dashboard',
}: CrashScreenProps) => {
  const showTechnicalDetails = import.meta.env.DEV && error != null;
  const { name, message, stack } = showTechnicalDetails
    ? toErrorParts(error)
    : { name: '', message: '', stack: undefined };

  // Only claim notification when we have a real Sentry event id. Local /
  // development / staging skip Sentry.init, so a missing id means nothing was
  // sent — do not invent a support code or say the team was notified.
  const resolvedDescription =
    description ??
    (eventId
      ? 'This page ran into an unexpected problem. Our team has been automatically notified. You can try again, or reload the page.'
      : 'This page ran into an unexpected problem. You can try again, or reload the page.');

  return (
    <div className='fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-background p-6 text-center'>
      <img src='/enrollio-logo.svg' alt='Enrollio' className='mb-2 h-10 w-auto' />

      <h1 className='text-2xl font-semibold tracking-tight'>{title}</h1>
      <p className='max-w-md text-muted-foreground'>{resolvedDescription}</p>

      <div className='mt-2 flex flex-wrap items-center justify-center gap-2'>
        {onRetry ? (
          <Button onClick={onRetry}>
            <RefreshCw className='size-4' />
            Try again
          </Button>
        ) : null}
        <Button
          variant={onRetry ? 'outline' : 'default'}
          onClick={() => {
            window.location.reload();
          }}
        >
          Reload page
        </Button>
        <Button
          variant='ghost'
          onClick={() => {
            window.location.href = homeHref;
          }}
        >
          {homeLabel}
        </Button>
      </div>

      {eventId ? (
        <p className='text-xs text-muted-foreground'>
          Reference code:{' '}
          <code className='rounded bg-muted px-1 py-0.5 font-mono'>
            {eventId}
          </code>
          <br />
          Please share this code with support to help us investigate.
        </p>
      ) : null}

      {showTechnicalDetails ? (
        <Collapsible className='mt-2 w-full max-w-lg text-left'>
          <CollapsibleTrigger className='text-xs font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground'>
            Technical details
          </CollapsibleTrigger>
          <CollapsibleContent className='mt-2'>
            <div className='space-y-2 rounded-md border border-border bg-muted/40 p-3'>
              <p className='break-words font-mono text-xs text-destructive'>
                {name}: {message}
              </p>
              {stack ? (
                <pre className='max-h-48 overflow-auto whitespace-pre-wrap break-words text-[11px] leading-relaxed text-muted-foreground'>
                  {stack}
                </pre>
              ) : null}
              {componentStack ? (
                <pre className='max-h-48 overflow-auto whitespace-pre-wrap break-words text-[11px] leading-relaxed text-muted-foreground'>
                  {componentStack}
                </pre>
              ) : null}
            </div>
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </div>
  );
};
